import React, { useCallback, useEffect } from "react";
import { useForm, Controller } from "react-hook-form";
import toast from "react-hot-toast";
import Box from "@mui/material/Box";
import Grid from "@mui/material/Grid";
import FormControl from "@mui/material/FormControl";
import FormHelperText from "@mui/material/FormHelperText";
import Drawer from "@mui/material/Drawer";
import TextField from "@mui/material/TextField";
import FormHeader from "views/components/formheader";
import FormButtons from "views/components/formbuttons";
import APICall from "network/APICall";
import EndPoints from "network/Endpoints";
import Loader from "@core/helper/Loader";

/**
 * MembershipPriceForm
 *
 * Drawer-based form used for both adding (first-time) and editing the
 * membership price.
 *
 * On edit, a new Stripe Product Price pair is created on the backend and the
 * existing record is marked inactive — Stripe Prices are immutable once
 * created.
 *
 * Props:
 *   open         - boolean, controls drawer visibility
 *   toggle       - function, closes the drawer
 *   type         - "add" | "edit"
 *   heading      - string, displayed in the drawer header
 *   activePrice  - object | null, the currently active membership price
 *   onSuccess    - callback invoked after successful save
 */
const MembershipPriceForm = ({
  open,
  toggle,
  type,
  heading,
  activePrice,
  onSuccess,
}) => {
  const defaultValues = { amount: "" };

  const {
    reset,
    control,
    clearErrors,
    handleSubmit,
    setValue,
    formState: { errors },
  } = useForm({ defaultValues });

  const resetToEmpty = useCallback(() => reset({ amount: "" }), [reset]);

  const resetToStored = useCallback(() => {
    if (type === "edit" && activePrice) {
      setValue("amount", activePrice.amount ?? "");
    }
  }, [type, activePrice, setValue]);

  useEffect(() => {
    if (open) {
      if (type === "edit") {
        resetToStored();
      } else {
        resetToEmpty();
      }
    }
  }, [open, type, resetToStored, resetToEmpty]);

  const handleClose = () => {
    clearErrors();
    reset(defaultValues);
    toggle();
  };

  const onSubmit = (values) => {
    Loader.isLoading(true);
    const payload = { amount: Number(values.amount) };

    APICall("post", payload, EndPoints.saveMembershipPrice)
      .then((response) => {
        const { isError, message } = response.data;
        if (!isError) {
          toast.success(message);
          if (onSuccess) onSuccess();
          handleClose();
        } else {
          toast.error(message);
        }
      })
      .catch((error) => toast.error(error))
      .finally(() => Loader.isLoading(false));
  };

  return (
    <Drawer
      open={open}
      anchor="right"
      variant="temporary"
      onClose={handleClose}
      ModalProps={{ keepMounted: true }}
      sx={{ "& .MuiDrawer-paper": { width: { xs: 300, sm: 400 } } }}
    >
      <FormHeader heading={heading} onClick={handleClose} />
      <Box sx={{ p: 5 }}>
        <form onSubmit={handleSubmit(onSubmit)}>
          <Grid container spacing={4}>
            <Grid item xs={12}>
              <FormControl fullWidth>
                <Controller
                  name="amount"
                  control={control}
                  rules={{
                    required: true,
                    validate: (v) =>
                      Number(v) > 0 || "Amount must be greater than 0",
                  }}
                  render={({ field: { value, onChange } }) => (
                    <TextField
                      value={value}
                      onChange={onChange}
                      fullWidth
                      type="number"
                      inputProps={{ min: "0.01", step: "0.01" }}
                      label="Membership Amount (AUD)"
                      error={Boolean(errors.amount)}
                      placeholder="e.g. 999.00"
                      aria-describedby="membership-amount-helper"
                    />
                  )}
                />
                {errors.amount && (
                  <FormHelperText
                    sx={{ color: "error.main" }}
                    id="membership-amount-helper"
                  >
                    {errors.amount.message || "This field is required"}
                  </FormHelperText>
                )}
              </FormControl>
            </Grid>

            <FormButtons type={type} resetFunction={resetToEmpty} />
          </Grid>
        </form>
      </Box>
    </Drawer>
  );
};

export default MembershipPriceForm;
